import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useNavigate } from 'react-router-dom';
import { Play, Star, TrendingUp, Users, Zap, Shield } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Play className="h-8 w-8" />,
      title: "Stream Instantly",
      description: "Watch your favorite content in HD quality with no buffering delays"
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Trending Content",
      description: "Stay updated with the latest shows and live sports events"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Multi-Device Access",
      description: "Enjoy seamless viewing across all your devices anywhere, anytime"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Premium Quality",
      description: "Experience cinema-quality streaming with advanced video technology"
    }
  ];

  const trendingShows = [
    { title: "Breaking Bad", genre: "Drama", rating: 9.5 },
    { title: "Stranger Things", genre: "Sci-Fi", rating: 8.7 },
    { title: "The Crown", genre: "Historical", rating: 8.9 },
    { title: "Ozark", genre: "Thriller", rating: 8.4 }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />

      <main className="flex-1 pt-14 sm:pt-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight">
                    Your Gateway to
                    <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                      Premium Entertainment
                    </span>
                  </h1>
                  <p className="text-xl text-slate-300 max-w-2xl leading-relaxed">
                    Discover trending TV shows, live football matches, and premium content.
                    Choose your path to start an unforgettable streaming experience.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/tv')}
                    className="group relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Play className="h-5 w-5" />
                      Explore TV Shows
                    </span>
                  </button>
                  <button
                    onClick={() => navigate('/football')}
                    className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Zap className="h-5 w-5" />
                      Watch Sports
                    </span>
                  </button>
                </div>
              </div>

              <div className="relative">
                <div className="relative rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl">
                  <div className="aspect-video bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl overflow-hidden relative">
                    <img
                      src="https://images.pexels.com/photos/7991225/pexels-photo-7991225.jpeg?auto=compress&cs=tinysrgb&w=800&h=450&fit=crop"
                      alt="Premium streaming content"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div
                      onClick={() => navigate('/tv')}
                      className="rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 p-4 hover:from-blue-500/30 hover:to-purple-500/30 transition-all duration-300 cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/tv'); }}
                    >
                      <div className="text-blue-300 text-sm font-medium">Trending</div>
                      <div className="text-white font-semibold mt-1">TV & shows</div>
                      <div className="text-xs text-slate-400 mt-1">1000+ titles</div>
                    </div>
                    <div
                      onClick={() => navigate('/football')}
                      className="rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-white/10 p-4 hover:from-green-500/30 hover:to-emerald-500/30 transition-all duration-300 cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/football'); }}
                    >
                      <div className="text-green-300 text-sm font-medium">Live</div>
                      <div className="text-white font-semibold mt-1">sports</div>
                      <div className="text-xs text-slate-400 mt-1">24/7 coverage</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Join millions of users who trust SceneShop for their entertainment needs.
              Start exploring premium content today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/tv')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              >
                Get Started Now
              </button>
              <button className="border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300">
                Learn More
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
